/**
 * Miettage und Staffelstufen.
 *
 * Bevor irgendein Preis faellt, muss feststehen, wie viele Tage berechnet
 * werden. Das ist in der Branche keine triviale Frage
 * (04-preisfindung.md, Abschnitt "Die Miettage").
 *
 * Regel 6: Belegung ist nicht Berechnung. Bei fuenf Kalendertagen steht das
 * Geraet ueber das Wochenende beim Kunden, wird dafuer aber nicht berechnet.
 * Der Kalender blockt trotzdem -- das entscheidet `verfuegbarkeit.ts`, nicht
 * diese Datei.
 */
const MS_JE_TAG = 86_400_000;
const MS_JE_STUNDE = 3_600_000;
/** Letzter Sonntag eines Monats, 01:00 UTC — die EU-Zeitumstellung. */
function umstellungUtc(jahr, monat0) {
    const letzter = new Date(Date.UTC(jahr, monat0 + 1, 0));
    const sonntag = letzter.getUTCDate() - letzter.getUTCDay();
    return Date.UTC(jahr, monat0, sonntag, 1);
}
/**
 * Abstand Berlin − UTC zu diesem Zeitpunkt: +2 h in der Sommerzeit (letzter
 * Sonntag im März bis letzter Sonntag im Oktober, jeweils 01:00 UTC), sonst
 * +1 h. Gerechnet, nicht über `Intl` nachgeschlagen — `tagNummer` steht in
 * Schleifen über jede Belegung und jeden Tag; der Test hält es gegen `Intl`.
 */
export function versatzBerlinMs(zeitpunkt) {
    const t = zeitpunkt.getTime();
    const jahr = zeitpunkt.getUTCFullYear();
    return t >= umstellungUtc(jahr, 2) && t < umstellungUtc(jahr, 9) ? 2 * MS_JE_STUNDE : MS_JE_STUNDE;
}
/**
 * Tage seit dem 01.01.1970 — **der Kalendertag in deutscher Zeit**. Der
 * Rechenwert fuer alles hier.
 *
 * Prüfrunde Interview F, N7-07 (05.10.2026): Bis dahin las diese Funktion
 * den UTC-Tag. Für einen Kalendertag als UTC-Mitternacht (`tag()`, `ausIso()`)
 * ist das dasselbe — für einen echten Zeitpunkt aus dem Ist nicht: Eine
 * Übergabe am 05.10. um 00:03 deutscher Zeit ist 04.10. 22:03 UTC und wurde
 * als Miettag 04.10. gezählt. Jetzt zählt der Tag, an dem es in Deutschland
 * war. UTC-Mitternacht ist in Berlin 01:00 bzw. 02:00 desselben Tages, also
 * bleibt jeder reine Kalendertag, was er war.
 */
export function tagNummer(datum) {
    return Math.floor((datum.getTime() + versatzBerlinMs(datum)) / MS_JE_TAG);
}
/** Rueckweg: Tagesnummer -> UTC-Mitternacht. */
export function ausTagNummer(nummer) {
    return new Date(nummer * MS_JE_TAG);
}
/** Samstag oder Sonntag. */
export function istWochenende(nummer) {
    const wochentag = new Date(nummer * MS_JE_TAG).getUTCDay();
    return wochentag === 0 || wochentag === 6;
}
/**
 * Zaehlt dieser Kalendertag nach den Regeln als Miettag?
 *
 * Der Sechstagekalender (Montag bis Samstag) steht in 04-preisfindung.md und
 * im Seed: `machine_category` 'Landtechnik' hat `calendar_days = 6`. Der Typ
 * `Kalenderregeln` laesst ihn heute nicht zu -- die Rechnung hier kann ihn
 * aber, damit das Erweitern des Typs eine Zeile bleibt und kein Umbau.
 */
export function zaehltAlsMiettag(nummer, regeln) {
    const kalendertage = regeln.kalendertage;
    if (kalendertage >= 7)
        return true;
    const wochentag = new Date(nummer * MS_JE_TAG).getUTCDay();
    if (kalendertage === 6)
        return wochentag !== 0;
    return wochentag !== 0 && wochentag !== 6;
}
/**
 * Miettage mit Begruendung.
 *
 * Die Reihenfolge ist festgelegt und hat einen Grund:
 *   1. Fenster von..bis, beide Tage eingeschlossen
 *   2. Wochenenden streichen, wenn fuenf Kalendertage gelten
 *   3. ersten bzw. letzten *gezaehlten* Tag streichen, wenn er nicht zaehlt
 *   4. auf die Mindestmietdauer anheben
 *
 * Schritt 3 arbeitet auf dem Ergebnis von Schritt 2, nicht auf dem Rohfenster:
 * Wer am Samstag ausgibt, gibt bei fuenf Kalendertagen am Montag den ersten
 * berechneten Tag -- und der ist gemeint, wenn der Anreisetag nicht zaehlt.
 * ANNAHME, weil die Doku diesen Fall nicht entscheidet.
 */
export function miettageErklaert(von, bis, regeln) {
    const ersterTag = tagNummer(von);
    const letzterTag = tagNummer(bis);
    const schritte = [];
    if (letzterTag < ersterTag) {
        return { tage: 0, schritte: [{ key: 'preis.leeres_fenster' }], tagnummern: [] };
    }
    const kalenderfenster = letzterTag - ersterTag + 1;
    let tage = [];
    for (let n = ersterTag; n <= letzterTag; n++) {
        if (zaehltAlsMiettag(n, regeln))
            tage.push(n);
    }
    const gestrichen = kalenderfenster - tage.length;
    if (gestrichen > 0) {
        schritte.push({
            key: 'preis.wochenende_faellt_weg',
            kalendertage: regeln.kalendertage,
            fenster: kalenderfenster,
            gestrichen,
        });
    }
    if (!regeln.ersterTagZaehlt && tage.length > 0) {
        tage = tage.slice(1);
        schritte.push({ key: 'preis.erster_tag_zaehlt_nicht' });
    }
    if (!regeln.letzterTagZaehlt && tage.length > 0) {
        tage = tage.slice(0, -1);
        schritte.push({ key: 'preis.letzter_tag_zaehlt_nicht' });
    }
    let ergebnis = tage.length;
    if (ergebnis < regeln.mindesttage) {
        schritte.push({ key: 'preis.mindesttage', mindest: regeln.mindesttage, statt: ergebnis });
        ergebnis = regeln.mindesttage;
    }
    schritte.push({ key: 'preis.miettage', wert: ergebnis });
    return { tage: ergebnis, schritte, tagnummern: tage };
}
/** Nur die Zahl. Die Begruendung liefert `miettageErklaert`. */
export function miettage(von, bis, regeln) {
    return miettageErklaert(von, bis, regeln).tage;
}
/**
 * Die fuenf Schwellen der Staffel, aus den Kalenderregeln abgeleitet.
 *
 * Die Liste staffelt 1-2 Tage | 3-5 Tage | ab 2. Woche | ab 3. Woche |
 * ab 4. Woche. Was "ab 2. Woche" in Miettagen heisst, haengt am Mietkalender:
 * bei fuenf Kalendertagen ist die erste Woche nach fuenf Miettagen um, bei
 * sieben nach sieben.
 *
 *   kalendertage = 5  ->  1 | 3 |  6 | 11 | 16
 *   kalendertage = 7  ->  1 | 3 |  8 | 15 | 22
 *
 * Die ersten beiden Stufen sind nicht abgeleitet, sondern gesetzt: "1-2 Tage"
 * und "3-5 Tage" stehen so auf der Liste vom 15.07.2026 und folgen keiner
 * Wochenlogik.
 */
export function staffelSchwellen(regeln) {
    const woche = regeln.kalendertage;
    return [1, 3, woche + 1, 2 * woche + 1, 3 * woche + 1];
}
/**
 * Welche Stufe gilt bei dieser Dauer -- geliefert als `from_day`.
 *
 * Ein Satz gilt fuer die ganze Dauer, nicht eine Summe aus Bloecken:
 * zehn Miettage Bomag sind 10 x 18,00 EUR, nicht zwei Wochenbloecke plus Rest.
 */
export function staffelStufe(miettageZahl, regeln) {
    const schwellen = staffelSchwellen(regeln);
    let gewaehlt = schwellen[0] ?? 1;
    for (const schwelle of schwellen) {
        if (miettageZahl >= schwelle)
            gewaehlt = schwelle;
    }
    return gewaehlt;
}
/**
 * Schnitt zweier Tagesfenster, beide Enden eingeschlossen.
 * Gibt `null`, wenn sie sich nicht beruehren. Braucht der Abrechnungslauf
 * fuer "Ist-Fenster geschnitten mit Abrechnungsperiode".
 */
export function fensterSchnitt(a, b) {
    const von = Math.max(tagNummer(a.von), tagNummer(b.von));
    const bis = Math.min(tagNummer(a.bis), tagNummer(b.bis));
    if (bis < von)
        return null;
    return { von: ausTagNummer(von), bis: ausTagNummer(bis) };
}
/**
 * Wie weit die Suche nach dem Ende hoechstens laeuft.
 *
 * Bei fuenf Kalendertagen kostet ein Miettag im Schnitt 1,4 Kalendertage; der
 * Zuschlag daneben faengt Feiertage und Randtage ab, die nicht zaehlen. Ohne
 * Deckel waere ein Kalender, der keinen einzigen Tag zaehlt, eine Schleife
 * ohne Ende.
 */
const ENDE_SUCHE_HOECHSTENS = (ziel) => ziel * 3 + 14;
/**
 * Das Ende eines Zeitraums, der so viele **Miettage** traegt.
 *
 * Der Rueckweg zu `miettageErklaert`: Dort steht ein Fenster und heraus kommt
 * eine Zahl, hier steht eine Zahl und heraus kommt das Fenster. „Ab Montag,
 * fuenf Miettage" reicht damit bis Freitag und nicht bis Mittwoch mit zwei
 * gestrichenen Tagen.
 *
 * **Warum es diese Richtung ueberhaupt braucht.** Luca, 17.09.2026, vor der
 * Preisauskunft: *„Thema Preisauskunft ist noch nicht perfekt, denn der
 * beruecksichtigt die Tage, also Wochenenden, nicht."* Die Maske rechnete
 * `bis = von + tage - 1` -- Kalendertage. Am Telefon meint aber niemand
 * Kalendertage: „fuenf Tage Radlader" heisst fuenf berechnete Tage.
 *
 * Die Randtage gehen mit: Zaehlt der Anreisetag nicht, muss ein Tag mehr
 * gelaufen werden, damit am Ende die gewuenschte Zahl uebrig bleibt -- genau
 * die beiden Schnitte, die `miettageErklaert` hinterher macht.
 *
 * **Die Mindestmietdauer bleibt draussen.** Sie hebt den *Preis* auf eine
 * Untergrenze (`preis.mindesttage`), sie verlaengert nicht die Miete: Wer
 * einen Tag will, bekommt einen Tag im Kalender und drei auf der Rechnung.
 */
export function endeFuerMiettage(von, gewuenscht, regeln) {
    const start = tagNummer(von);
    const ziel = Math.max(1, Math.floor(gewuenscht));
    // Was `miettageErklaert` hinterher vorn und hinten abschneidet, muss hier
    // zusaetzlich gelaufen werden.
    const noetig = ziel + (regeln.ersterTagZaehlt ? 0 : 1) + (regeln.letzterTagZaehlt ? 0 : 1);
    let gezaehlt = 0;
    const deckel = start + ENDE_SUCHE_HOECHSTENS(noetig);
    for (let n = start; n <= deckel; n += 1) {
        if (zaehltAlsMiettag(n, regeln))
            gezaehlt += 1;
        if (gezaehlt >= noetig)
            return ausTagNummer(n);
    }
    // Kein Kalender zaehlt so wenig, dass es hierher kommt. Wenn doch, ist das
    // Ende der Deckel und nicht eine Endlosschleife.
    return ausTagNummer(deckel);
}
// ---------------------------------------------------------------------------
// Die Kalenderwoche — die grobe Vorplanung (Migration 0154)
// ---------------------------------------------------------------------------
/**
 * **Der Montag der Woche, in der dieser Tag liegt** — ISO 8601.
 *
 * Luca, 21.09.2026: *„Man macht die grobe Vorplanung mit KW im Angebot,
 * braucht kein festes Datum, und dann bei der Ortsaufnahme sagt man: Das kann
 * ein genaues Datum sein."*
 *
 * Eine Position mit `schedule_precision = 'kw'` trägt Montag und Sonntag
 * derselben Woche als Zeitraum — kein zweites Datumsfeld, damit das Brett sie
 * liest wie jede andere Zeile. Diese Funktion ist die eine Stelle, an der aus
 * „irgendein Tag" der Anfang seiner Woche wird: Die Route normalisiert damit,
 * die Maske zeigt damit an, und der Riegel `item_kw_ist_eine_woche` prüft
 * dasselbe in der Datenbank nach.
 */
export function wochenanfang(nummer) {
    // `getUTCDay`: 0 = Sonntag. ISO zählt Montag als ersten Tag.
    const wochentag = new Date(nummer * MS_JE_TAG).getUTCDay();
    return nummer - ((wochentag + 6) % 7);
}
/**
 * **Die Nummer der Kalenderwoche und ihr Jahr** — ISO 8601.
 *
 * Nicht „die wievielte Woche seit dem 1. Januar": Die ISO-Woche gehört dem
 * Jahr, in dem ihr Donnerstag liegt. Der 1. Januar 2027 ist ein Freitag und
 * liegt damit in KW 53 von 2026 — wer das anders rechnet, schreibt im
 * Dezember „KW 1" auf ein Angebot und niemand weiß, welches Jahr gemeint ist.
 */
export function kalenderwoche(nummer) {
    // Der Donnerstag derselben Woche entscheidet über das Jahr.
    const donnerstag = wochenanfang(nummer) + 3;
    const datum = new Date(donnerstag * MS_JE_TAG);
    const jahr = datum.getUTCFullYear();
    const ersterDonnerstag = wochenanfang(tagNummer(new Date(Date.UTC(jahr, 0, 4)))) + 3;
    return { jahr, woche: Math.round((donnerstag - ersterDonnerstag) / 7) + 1 };
}
/**
 * Montag und Sonntag der Woche, in der `datum` liegt — als Zeitraum.
 *
 * Die Form, die `rental_order_item` bei `schedule_precision = 'kw'` trägt:
 * `bis` ist der **letzte** Tag der Woche, nicht der erste danach — dieselbe
 * Lesart wie überall sonst an einer Position.
 */
export function wochenfenster(datum) {
    const anfang = wochenanfang(tagNummer(datum));
    return { von: ausTagNummer(anfang), bis: ausTagNummer(anfang + 6) };
}
/**
 * **Ein grober Zeitraum über mehrere Wochen** — Montag der ersten bis Sonntag
 * der letzten (Systemprüfung W1, Entscheidung (2), Migration 0188).
 *
 * Lisa: *„bei der Annahme schon einen groben Zeitraum eingrenzen (z. B. ‚in
 * den nächsten zwei Wochen')."* Luca hat entschieden, dafür **kein** zweites
 * Datumsfeld und keinen neuen Wert zu nehmen, sondern die KW auf einen Bereich
 * zu erweitern: `schedule_precision = 'kw'` heißt jetzt „ganze Wochen", und der
 * Riegel `item_kw_ganze_wochen` prüft dasselbe wie diese Funktion.
 *
 * Ohne `bis` — oder mit einem `bis` vor `von` — ist es die eine Woche von
 * `von`, genau wie `wochenfenster`. Ein Aufrufer, der sich im Tag vertut, bekommt
 * so nie einen Zeitraum, der rückwärts läuft.
 */
export function wochenbereich(von, bis) {
    const anfang = wochenanfang(tagNummer(von));
    const letzte = bis ? wochenanfang(tagNummer(bis)) : anfang;
    return { von: ausTagNummer(anfang), bis: ausTagNummer(Math.max(anfang, letzte) + 6) };
}
//# sourceMappingURL=kalender.js.map